// ─────────────────────────────────────────────
//  GetStartedPage — the fork between a private sandbox and a real organization.
//
//  ORDERING MATTERS AND IS NOT COSMETIC: the Firebase Auth account is created
//  HERE, after the choice, never on the login screen. Creating it earlier lets
//  AuthContext's bootstrap write a users/{uid} document into the hospital's
//  Firestore in the gap before a mode is chosen — which is exactly the
//  guarantee the sandbox exists to keep.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { signUp } from "../firebase/auth";
import { enterSandbox, leaveSandbox } from "../sandbox/mode";
import { Card, Button } from "../components";
import tokens from "../theme/tokens";

const { color, space, font, radius } = tokens;

// A FULL RELOAD, not a route change: config.js picks the data store once at
// boot, so a client-side navigation would leave the app pointed at real
// Firestore while holding a sandbox profile. Injectable so tests can observe
// the reload without navigating jsdom.
const hardReload = (path) => { window.location.assign(path); };

export default function GetStartedPage({ reload = hardReload }) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  // The taken-address case is the only failure whose remedy is a different
  // screen, so it gets its own affordance rather than a sentence telling the
  // user to find their way back. See the button below.
  const [taken, setTaken] = useState(false);

  if (!state?.email || !state?.password) return <Navigate to="/login" replace />;

  const describe = (err) =>
    err?.code === "auth/email-already-in-use"
      ? "That address already has an account. Sign in instead."
      : err?.code === "auth/weak-password"
      ? "That password is too weak. Choose at least 8 characters."
      : err?.code === "auth/invalid-email"
      ? "That email address is not valid."
      : "Could not create the account. Please try again.";

  const chooseSolo = async () => {
    setError("");
    setTaken(false);
    setBusy("solo");
    // Flag first, account second. A reload follows, and config.js reads the
    // flag at boot to hand the app a local store instead of Firestore.
    //
    // ⚠️ AND IT MUST COME BACK DOWN ON EVERY PATH THAT DOES NOT REACH THAT
    // RELOAD. `enterSandbox()` is a localStorage write: it OUTLIVES this
    // component, this route and this browser session. The version without the
    // rollback showed the error, left the key at "1", and the user — most often
    // someone who already had an account and typed their real credentials into
    // the Create Account form, which is the form /login opens with
    // (LoginPage.js: `useState("new")`) — went back, signed in for real, and
    // booted onto the LOCAL store. No error, no empty state, no way out:
    // nothing on this path calls `leaveSandbox()`, so their hospital's board
    // was simply gone. It reads exactly like "the app lost all our data".
    //
    // The rollback is unconditional and is NOT guarded by an is-still-mounted
    // check: the flag is durable even when the screen is not, so an aborted
    // navigation or an unmount mid-request has to clear it too.
    enterSandbox();
    try {
      await signUp(state.email, state.password);
    } catch (err) {
      leaveSandbox();
      setError(describe(err));
      setTaken(err?.code === "auth/email-already-in-use");
      setBusy("");
      return;
    }
    // Outside the try on purpose. reload() throws under jsdom and a real
    // browser can abort a navigation; inside the try that throw would be
    // reported as "could not create the account" and would ROLL BACK the flag
    // for an account that now exists and is already signed in — the mirror
    // image of the bug above.
    reload("/welcome");
  };

  const chooseOrg = () => navigate("/go-live", { state });

  const settingUp = busy === "solo";

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <Card variant="elevated">
          <h1 style={styles.title}>How do you want to start?</h1>
          {error ? <div style={styles.error} role="alert">{error}</div> : null}

          {/* "Sign in instead" is only useful if it is reachable. Without this
              control the taken-address message named an action with nothing to
              act on: /get-started has no navigation of its own, so the only way
              back to the sign-in form was the browser's Back button — which a
              phone in a hallway barely offers. It carries the address forward
              and asks LoginPage to open on SIGN IN, because sending someone who
              already has an account back to the Create Account form is how they
              arrived here in the first place. */}
          {taken ? (
            <button type="button" style={styles.link}
                    onClick={() => navigate("/login", { state: { mode: "existing", email: state.email } })}>
              Sign in instead
            </button>
          ) : null}

          <section style={styles.option}>
            <h2 style={styles.optionTitle}>Test Solo</h2>
            <p style={styles.optionBody}>
              A private version of ATC on this phone. Build your own operating
              rooms, add providers and run the board. Nothing is shared and
              nothing leaves the device.
            </p>
            {/* Button renders "…" while loading, which would leave the control
                with no accessible name; the label is restored explicitly. */}
            <Button variant="primary" size="full" onClick={chooseSolo}
                    loading={settingUp} disabled={!!busy}
                    ariaLabel={settingUp ? "Setting up your private version" : undefined}>
              {settingUp ? "Setting up…" : "Test Solo"}
            </Button>
          </section>

          <section style={styles.option}>
            <h2 style={styles.optionTitle}>Set Up Organization</h2>
            <p style={styles.optionBody}>
              For a hospital or practice with a team. Tell us about your
              organization and we will set up your shared workspace.
            </p>
            <Button variant="secondary" size="full" onClick={chooseOrg} disabled={!!busy}>
              Set Up Organization
            </Button>
          </section>
        </Card>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh", background: color.surface.base, display: "flex",
    alignItems: "center", justifyContent: "center", padding: space.md,
  },
  shell: { width: 420, maxWidth: "95vw" },
  title: {
    fontSize: font.size.display, fontWeight: font.weight.bold,
    color: color.accent.primary, fontFamily: font.family,
    textAlign: "center", marginBottom: space.lg,
  },
  option: { marginBottom: space.lg },
  optionTitle: {
    fontSize: font.size.body, fontWeight: font.weight.bold,
    color: color.text.primary, fontFamily: font.family, marginBottom: space.xs,
  },
  optionBody: {
    fontSize: font.size.label, color: color.text.secondary,
    fontFamily: font.family, lineHeight: font.lineHeight.normal,
    marginBottom: space.sm,
  },
  // surface.error is the shared error ground; accent.error on it is 4.95:1,
  // clearing AA at this 12px label size. (It does NOT clear on surface.raised,
  // 3.83:1 — see the note on the token.)
  error: {
    background: color.surface.error,
    border: "1px solid " + color.accent.error,
    color: color.accent.error, borderRadius: radius.sm, padding: "8px 12px",
    fontSize: font.size.label, marginBottom: space.md, fontFamily: font.family,
  },
  // Same shape as LoginPage's inline links: 44 px minimum touch target
  // (WCAG 2.2 AA), with the padding centring the underlined text inside it.
  link: {
    display: "block", minHeight: 44, width: "100%", padding: space.sm,
    margin: `0 auto ${space.md}px`, background: "transparent", border: "none",
    color: color.accent.primary, cursor: "pointer",
    fontSize: font.size.label, fontFamily: font.family, textDecoration: "underline",
  },
};
